/**
 * DomainShell — page layout for a single Retail admin domain (Identity, Products, System, ...).
 *
 * Redesign 0.53.0: the shared page header (H1 + explanation + the domain's header actions) and
 * the sub-catalog tabs, which scroll sideways on a phone. The same pages are also in the sidebar.
 * Visible only when the user has at least one role that authorizes the domain.
 */

import { NavLink, Outlet } from 'react-router';
import type { ReactNode } from 'react';
import { PageHeader } from '@retail-ops/panel-shell';

export interface DomainSubNavItem {
  /** Route segment, e.g. 'users'. Renders under /<domain>/<slug>. */
  slug: string;
  /** Tab label. */
  label: string;
  /** Optional roles that gate the tab (any-of). When unset, always visible. */
  requiredRoles?: ReadonlyArray<string>;
}

export interface DomainShellProps {
  title: ReactNode;
  description?: ReactNode;
  /** Sub-nav items in display order. */
  items: ReadonlyArray<DomainSubNavItem>;
  /** Current user's role codes; controls per-tab visibility. */
  userRoles: ReadonlyArray<string>;
  /** Path prefix the sub-nav links resolve against, e.g. '/identity'. */
  basePath: string;
  /** Optional right-aligned header action buttons (e.g. Sync, Ignored). */
  headerActions?: ReactNode;
}

export function DomainShell({ title, description, items, userRoles, basePath, headerActions }: DomainShellProps): JSX.Element {
  const visible = items.filter(
    (it) => !it.requiredRoles || it.requiredRoles.length === 0 || it.requiredRoles.some((r) => userRoles.includes(r)),
  );

  return (
    <div className="domain-shell">
      <PageHeader title={title} intro={description} actions={headerActions} />
      {visible.length > 1 && (
        <nav className="domain-shell__subnav" aria-label="Pages in this area">
          {visible.map((it) => (
            <NavLink key={it.slug} to={`${basePath}/${it.slug}`} end={it.slug === ''}>
              {it.label}
            </NavLink>
          ))}
        </nav>
      )}
      <div className="domain-shell__body">
        <Outlet />
      </div>
    </div>
  );
}
